import { Alert, Button, Modal } from "@xola/ui-kit";
import { DateFormat, formatDate, isAllDay, requiredString } from "common";
import { cloneDeep, isNil } from "lodash";
import React, { useState } from "react";
import { Except } from "type-fest";
import {
    BaseScheduleRepeat,
    BaseScheduleType,
    Experience,
    Permission,
    PriceVariationType,
    ScheduleDeparture,
    ScheduleType,
} from "types";
import { array, number, object, ref, string } from "yup";
import { AnySchema } from "yup/lib";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { getDefaultScheduleForExperience } from "../../../models/Schedule";
import { PriceVariationPayload } from "../../../types/PriceVariationPayload";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ExperiencePricingFormPayload } from "../pricing/ExperiencePricingFormHelper";
import { EXPERIENCE_OPTION_PRIORITY_MAX, EXPERIENCE_OPTION_PRIORITY_MIN } from "./ExperienceOptionPicker";
import { BaseFormValues, ScheduleEditor, ScheduleFormValues } from "./ScheduleEditor";
import { ScheduleEditorSlotsProps } from "@/components/Product/SchedulesAvailability/useScheduleEditorSlots";

type Validations = Record<string, AnySchema>;

interface ScheduleEditorModalProps<FormValues extends BaseFormValues = BaseFormValues>
    extends ScheduleEditorSlotsProps {
    isOpen: boolean;
    title: string;
    description?: string;
    initialValues: ScheduleFormValues<FormValues>;
    blackoutSchedules?: boolean;
    onClose: () => void;
    onApply: (values: ScheduleFormValues<FormValues>, priceVariationPayload?: PriceVariationPayload) => void;
    // Runs before the modal closes; false keeps it open with the seller's edits.
    onBeforeApply?: (values: ScheduleFormValues<FormValues>) => Promise<ScheduleFormValues<FormValues> | false>;
    disablePastDays?: boolean;
    experience?: Experience;
    isNotResourceSchedule?: boolean;
    errorMessage?: string | null;
    isApplyUnavailable?: boolean;
    startEndDateFormat?: DateFormat;
    returnDateTime?: boolean;
    validations?: Validations;
    showAvailability?: boolean;
}

const TIMESLOTS_PER_SCHEDULE_LIMIT = 100;

export const getPayload = <FormValues extends BaseFormValues = BaseFormValues>(
    values: ScheduleFormValues<FormValues>,
    returnDateTime?: boolean,
): ScheduleFormValues<FormValues> => {
    // A blackout carries no Option; forms without an options field keep sending none.
    const options = values.type === BaseScheduleType.UNAVAILABLE && values.options ? [] : values.options;
    if (returnDateTime) {
        return { ...values, options };
    }

    const isCustomRepeat = values.repeat === BaseScheduleRepeat.CUSTOM;

    return {
        ...values,
        ...(!isCustomRepeat && {
            start: values.start ? formatDate(values.start) : null,
        }),
        end: isCustomRepeat ? null : values.end ? formatDate(values.end) : null,
        dates: isCustomRepeat ? values.dates : undefined,
        options,
    };
};

export const ScheduleEditorModal = <FormValues extends BaseFormValues = BaseFormValues>(
    props: ScheduleEditorModalProps<FormValues>,
) => {
    const handleClose = () => {
        props.onClose();
    };

    return (
        <Modal isOpen={props.isOpen} size="medium" onClose={handleClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

const getValidationSchema = (
    scheduleType?: ScheduleType,
    availabilityScheduleCount = 0,
    extraValidations?: Validations,
) => {
    return object({
        departure: string().test("DepartureCheck", "Invalid", (value, validationContext) => {
            const { createError, parent } = validationContext;

            if (parent.type === BaseScheduleType.UNAVAILABLE) {
                return true;
            }

            if (
                scheduleType === ScheduleType.TIME &&
                availabilityScheduleCount > 0 &&
                value === ScheduleDeparture.VARIES
            ) {
                return createError({
                    message:
                        "You cannot have a combination of all-day schedules and time-based schedules. If you would like to create an all-day schedule, please remove all time-based schedules from this product.",
                });
            }

            // When schedule type is "day" for the experience, show error only if availability schedules are configured,
            // since we can have time based schedules along with regular blackout schedules (similar to v1)
            if (
                scheduleType === ScheduleType.DAY &&
                availabilityScheduleCount > 0 &&
                value === ScheduleDeparture.FIXED
            ) {
                return createError({
                    message:
                        "You cannot have a combination of all-day schedules and time-based schedules. If you would like to create a time-based schedule, please remove all the all-day schedules from this product.",
                });
            }

            return true;
        }),
        times: array().test("TimesCheck", "Invalid", (value, validationContext) => {
            const { createError, parent } = validationContext;

            if (parent.type === BaseScheduleType.UNAVAILABLE) {
                return true;
            }

            const isFixedDeparture = parent.departure === ScheduleDeparture.FIXED;

            if (isFixedDeparture && (!value || (value && value.length === 0))) {
                return createError({ message: "Must have timeslots" });
            }

            if (isFixedDeparture && value && value.length >= TIMESLOTS_PER_SCHEDULE_LIMIT) {
                return createError({
                    message: `Cannot support more than ${TIMESLOTS_PER_SCHEDULE_LIMIT - 1} timeslots per schedule`,
                });
            }

            const duplicates = value?.filter((item: string, index) => index !== value.indexOf(item)) ?? [];

            if (duplicates.length > 0) {
                return createError({ message: "Cannot have duplicate timeslots" });
            }

            return true;
        }),
        days: array().when("repeat", {
            is: BaseScheduleRepeat.WEEKLY,
            then: array().required("Required").min(1, "Must have days"),
        }),
        dates: array().when("repeat", {
            is: BaseScheduleRepeat.CUSTOM,
            then: array().required("Required").min(1, "Must have specific dates"),
        }),
        timeRanges: array(
            object({
                startTime: number().required("Required"),
                endTime: number()
                    .required("Required")
                    .when("startTime", {
                        is: (startTime?: number) => !isNil(startTime),
                        then: number().moreThan(ref("startTime"), "Must be after start time"),
                    }),
            }),
        ),
        start: string().nullable().when("repeat", {
            is: BaseScheduleRepeat.WEEKLY,
            then: string().nullable(),
        }),
        options: array().when("type", {
            is: BaseScheduleType.UNAVAILABLE,
            then: array().notRequired(),
            otherwise: array()
                .of(
                    object({
                        optionId: requiredString("Select an Experience Option"),
                        priority: number().min(EXPERIENCE_OPTION_PRIORITY_MIN).max(EXPERIENCE_OPTION_PRIORITY_MAX),
                    }),
                )
                .max(1)
                .notRequired(),
        }),
        ...extraValidations,
    });
};

const ModalContent = <FormValues extends BaseFormValues = BaseFormValues>({
    initialValues,
    title,
    description,
    onClose,
    onApply,
    onBeforeApply,
    disablePastDays,
    blackoutSchedules,
    experience,
    isNotResourceSchedule,
    errorMessage,
    isApplyUnavailable,
    validations,
    showAvailability,
    returnDateTime,
    ...rest
}: Except<ScheduleEditorModalProps<FormValues>, "isOpen">) => {
    const { isGranted } = useAuthorization();
    const hasRequiredPermission = isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW);

    initialValues = initialValues ?? getDefaultScheduleForExperience(experience, blackoutSchedules);
    const [priceVariation, setPriceVariation] = useState<PriceVariationPayload>();

    const handleSubmit = async (values: ScheduleFormValues<FormValues>) => {
        if (errorMessage && !hasRequiredPermission && isApplyUnavailable) {
            onClose();
            return;
        }

        let formValues = getPayload(values, returnDateTime);
        if (onBeforeApply) {
            const confirmedValues = await onBeforeApply(formValues);
            if (!confirmedValues) {
                return;
            }
            formValues = confirmedValues;
        }

        onApply(formValues, priceVariation);
        onClose();
    };

    const basePriceVariation = getBasePriceVariation(experience);
    const availabilitySchedules =
        experience?.schedules.filter((schedule) => schedule.type === BaseScheduleType.AVAILABLE) ?? [];
    return (
        <XolaFormik<ScheduleFormValues<FormValues>>
            enableReinitialize
            validationSchema={getValidationSchema(experience?.scheduleType, availabilitySchedules.length, validations)}
            initialValues={cloneDeep(initialValues)}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit, setValues, errors, submitCount }) => (
                <>
                    <Modal.Header description={description}>{title}</Modal.Header>

                    <Modal.Body>
                        {errorMessage && isApplyUnavailable && errorMessage.length > 0 && (
                            <Alert className="mb-5" color="danger">
                                {errorMessage}
                            </Alert>
                        )}

                        <ScheduleEditor<FormValues>
                            inModal
                            // TODO: Remove `as any` and fix the type error
                            values={values as any}
                            errors={errors as any}
                            submitCount={submitCount}
                            disablePastDays={disablePastDays}
                            blackoutSchedules={blackoutSchedules}
                            isNotResourceSchedule={isNotResourceSchedule}
                            basePriceVariation={basePriceVariation}
                            showAvailability={showAvailability}
                            disableBlackoutTimeRanges={experience && isAllDay(experience)}
                            returnDateTime={returnDateTime}
                            experience={experience}
                            onChange={setValues}
                            onPriceVariationChange={(priceVariation, purchaseRuleLink) =>
                                setPriceVariation({
                                    priceVariation: priceVariation,
                                    purchaseRuleLink: purchaseRuleLink,
                                })
                            }
                            {...rest}
                        />
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton
                            data-testid="base-schedule-editor-modal-cancel-button"
                            onClick={() => onClose()}
                        >
                            Cancel
                        </SecondaryButton>

                        <Button
                            type="button"
                            data-testid="base-schedule-editor-modal-apply-button"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            {errorMessage && !hasRequiredPermission && isApplyUnavailable ? "Ok" : "Apply"}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getBasePriceVariation = (experience?: Experience | ExperiencePricingFormPayload) => {
    if (
        experience &&
        "publicPricing" in experience &&
        experience.publicPricing.terms[0].demographicPrices.length > 0 &&
        experience.privatePricing.terms[0].demographicPrices.length > 0
    ) {
        return {
            name: "",
            type: PriceVariationType.BASE,
            pricingGroups: [experience.publicPricing, experience.privatePricing],
        };
    }
};
